/* global localStorage */
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Wand2, ExternalLink, Copy, Check, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { getRcCode } from "@/data/rcCodeLookup";

const safe = (k) => {
  try { return JSON.parse(localStorage.getItem(k) || "null"); } catch { return null; }
};

/** Builds [{label, value, code}] rows from the current MTM draft. */
function buildSpec() {
  const general = safe("mtm_order") || safe("mtm_general") || {};
  const fabric = safe("mtm_fabric") || {};
  const measurements = safe("mtm_measurements") || {};
  const styles = safe("mtm_styles") || {};
  const rows = [];

  // Identification
  const customer = general.customerName || general.client_name || general.customer_name || "";
  if (customer) rows.push({ label: "Customer", value: customer, code: "" });

  // Fabric / lining / buttons (code goes in 'value' since they ARE codes)
  if (fabric?.fabricSelection?.rc_code) rows.push({ label: "Fabric", value: fabric.fabricSelection.rc_code, code: "" });
  if (fabric?.liningSelection?.rc_code) rows.push({ label: "Lining", value: fabric.liningSelection.rc_code, code: "" });
  if (fabric?.pipingSelection?.rc_code) rows.push({ label: "Piping", value: fabric.pipingSelection.rc_code, code: "" });
  if (fabric?.jacketButtonSelection?.code) rows.push({ label: "Jacket Button", value: fabric.jacketButtonSelection.code, code: "" });
  if (fabric?.vestButtonSelection?.code) rows.push({ label: "Vest Button", value: fabric.vestButtonSelection.code, code: "" });
  if (fabric?.shirtButtonSelection?.code) rows.push({ label: "Shirt Button", value: fabric.shirtButtonSelection.code, code: "" });
  if (fabric?.constructionType) rows.push({ label: "Construction", value: fabric.constructionType, code: getRcCode("canvas_construction", fabric.constructionType) });

  // Styles (jacket / pant / vest)
  const STYLE_FIELDS = {
    jacketStyling: [
      ["lapel_type", "Lapel Type"],
      ["lapel_width", "Lapel Width"],
      ["front_closure", "Front Closure"],
      ["vent", "Vent"],
      ["waist_pocket_type", "Waist Pocket"],
      ["waist_pocket_angle", "Pocket Angle"],
      ["ticket_pocket", "Ticket Pocket"],
      ["chest_pocket", "Chest Pocket"],
      ["lapel_buttonhole", "Lapel Buttonhole"],
      ["back_vent_length", "Back Vent Length"],
    ],
    vestStyling: [
      ["lapel", "Vest Lapel"],
    ],
  };
  Object.entries(STYLE_FIELDS).forEach(([key, fields]) => {
    const block = styles[key] || {};
    fields.forEach(([fld, label]) => {
      const val = block[fld];
      if (!val) return;
      const lookupKey = key === "vestStyling" && fld === "lapel" ? "vest_lapel" : fld;
      rows.push({ label, value: val, code: getRcCode(lookupKey, val) });
    });
  });

  // Measurements — flatten the most-used keys
  const m = measurements?.values || measurements || {};
  ["Chest","Stomach","Seat","Shoulders","Neck","Sleeve","Sleeve Length","Back Length","Front Length","Waist","Hip","Thigh","Knee","Bottom","Inseam","Outseam","Cuff","Bicep"].forEach((label) => {
    const k = Object.keys(m).find((x) => x.toLowerCase() === label.toLowerCase());
    const final = k && (m[k]?.final ?? m[k]?.value ?? m[k]);
    if (final !== undefined && final !== "" && typeof final !== "object") {
      rows.push({ label, value: String(final), code: "" });
    }
  });

  return rows;
}

/** The bookmarklet code itself — runs in the rcmtm tab.
 *  Reads `#mtm=<base64>` from the URL, decodes, shows a draggable floating
 *  panel with one row per spec entry. Clicking a row fills the currently
 *  focused input/select on the portal page. */
const BOOKMARKLET = `javascript:(function(){try{var h=location.hash.match(/mtm=([^&]+)/);if(!h){alert("No MTM data found in URL hash. Open the portal via Admin → RCMTM Portal → Open with Auto-Fill.");return;}var spec=JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(h[1])))));var prev=document.getElementById("mtm-helper-panel");if(prev)prev.remove();var lastInput=null;document.addEventListener("focusin",function(e){if(e.target&&e.target.matches&&e.target.matches("input,textarea,select"))lastInput=e.target;});var p=document.createElement("div");p.id="mtm-helper-panel";p.style.cssText="position:fixed;top:20px;right:20px;width:320px;max-height:80vh;background:#0f172a;color:#fff;border:1px solid #475569;border-radius:8px;z-index:2147483647;font-family:system-ui,sans-serif;font-size:12px;overflow:hidden;display:flex;flex-direction:column;box-shadow:0 8px 24px rgba(0,0,0,0.5)";var hd=document.createElement("div");hd.style.cssText="padding:10px;background:#020617;cursor:move;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #1e293b";hd.innerHTML='<strong style="color:#fbbf24">⚡ MTM Auto-Fill ('+spec.length+')</strong><span style="color:#64748b;font-size:10px">click input, then Fill</span><button id="mtmClose" style="background:transparent;color:#fff;border:none;cursor:pointer;font-size:18px;line-height:1">×</button>';p.appendChild(hd);hd.querySelector("#mtmClose").onclick=function(){p.remove()};var dx=0,dy=0,drag=false;hd.onmousedown=function(e){if(e.target.id==="mtmClose")return;drag=true;dx=e.clientX-p.offsetLeft;dy=e.clientY-p.offsetTop;};document.onmousemove=function(e){if(!drag)return;p.style.left=(e.clientX-dx)+"px";p.style.top=(e.clientY-dy)+"px";p.style.right="auto";};document.onmouseup=function(){drag=false;};var body=document.createElement("div");body.style.cssText="overflow-y:auto;flex:1";p.appendChild(body);function fill(v){if(!lastInput){alert("Click an input/textarea on the page first, then click Fill.");return;}try{var d=Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype,"value")||Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype,"value");d.set.call(lastInput,v);}catch(_e){lastInput.value=v;}lastInput.dispatchEvent(new Event("input",{bubbles:true}));lastInput.dispatchEvent(new Event("change",{bubbles:true}));lastInput.focus();}spec.forEach(function(r){var row=document.createElement("div");row.style.cssText="padding:6px 10px;border-top:1px solid #1e293b;display:flex;gap:6px;align-items:center";var info=document.createElement("div");info.style.cssText="flex:1;min-width:0";info.innerHTML='<div style="color:#94a3b8;font-size:10px">'+r.label+'</div><div style="display:flex;gap:6px;align-items:center"><span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">'+r.value+'</span>'+(r.code?'<code style="color:#fbbf24;font-size:10px;font-family:ui-monospace,monospace">'+r.code+'</code>':"")+'</div>';row.appendChild(info);if(r.code){var bC=document.createElement("button");bC.textContent="Fill code";bC.style.cssText="padding:3px 6px;background:#fbbf24;color:#000;border:none;border-radius:3px;cursor:pointer;font-size:10px;white-space:nowrap";bC.onclick=function(){fill(r.code)};row.appendChild(bC);}var bV=document.createElement("button");bV.textContent="Fill val";bV.style.cssText="padding:3px 6px;background:#334155;color:#fff;border:none;border-radius:3px;cursor:pointer;font-size:10px;white-space:nowrap";bV.onclick=function(){fill(r.value)};row.appendChild(bV);body.appendChild(row);});document.body.appendChild(p);}catch(err){alert("Auto-Fill error: "+err.message);}})();`;

export const PortalAutoFill = ({ portalUrl }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const spec = useMemo(() => buildSpec(), [open]);

  // React 18+ refuses to render `javascript:` URLs in `href`. Bypass the
  // sanitizer by setting the attribute imperatively the moment the element
  // is mounted (callback ref fires *after* the Radix Dialog portal mount).
  const setBookmarkletHref = (el) => {
    if (el) el.setAttribute("href", BOOKMARKLET);
  };

  const encodedHash = useMemo(() => {
    // base64 of JSON; URL-encoded to be safe in the hash
    try {
      const json = JSON.stringify(spec);
      // btoa needs latin1 → use the unicode-safe pattern
      const b64 = btoa(unescape(encodeURIComponent(json)));
      return encodeURIComponent(b64);
    } catch {
      return "";
    }
  }, [spec]);

  const openWithCodes = () => {
    if (!portalUrl) return;
    if (spec.length === 0) {
      toast.error("No active MTM draft. Start an order first.");
      return;
    }
    const sep = portalUrl.includes("#") ? "&" : "#";
    const url = `${portalUrl}${sep}mtm=${encodedHash}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Portal opened in new tab. Click your saved MTM Auto-Fill bookmarklet to start filling.");
  };

  const copyBookmarklet = async () => {
    try {
      await navigator.clipboard.writeText(BOOKMARKLET);
      setCopied(true);
      toast.success("Bookmarklet copied. Paste into a new bookmark's URL field.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="w-full justify-start"
        data-testid="autofill-open-btn"
      >
        <Wand2 size={12} className="mr-2 text-amber-400" />
        Auto-Fill into Portal
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 size={16} className="text-amber-400" /> RCMTM Auto-Fill
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2 text-sm">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3 flex gap-2">
              <Info size={14} className="text-amber-400 shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                Browsers block scripts from typing into another website. The
                workaround: install a one-time <strong>bookmarklet</strong>{" "}
                that runs inside the RCMTM tab and types each field for you.
              </p>
            </div>

            {/* Step 1 */}
            <div>
              <p className="text-xs font-medium mb-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 mr-1">1</span>
                Install the bookmarklet (one-time)
              </p>
              <p className="text-xs text-muted-foreground mb-2">
                Show your bookmarks bar (<kbd className="px-1 bg-muted">Ctrl/Cmd + Shift + B</kbd>), then{" "}
                <strong>drag the button below onto it</strong>. Or click "Copy
                code" and paste it as a new bookmark URL.
              </p>
              <div className="flex gap-2 items-center">
                <a
                  ref={setBookmarkletHref}
                  // eslint-disable-next-line react/no-unknown-property
                  onClick={(e) => e.preventDefault()}
                  draggable
                  className="px-3 py-2 bg-amber-500 text-black text-xs font-medium rounded cursor-grab active:cursor-grabbing select-none inline-flex items-center gap-1"
                  data-testid="autofill-bookmarklet-link"
                >
                  <Wand2 size={12} /> MTM Auto-Fill ← drag me
                </a>
                <Button size="sm" variant="outline" onClick={copyBookmarklet} data-testid="autofill-copy-bookmarklet">
                  {copied ? <Check size={12} className="mr-1 text-emerald-400" /> : <Copy size={12} className="mr-1" />}
                  Copy code
                </Button>
              </div>
            </div>

            {/* Step 2 */}
            <div>
              <p className="text-xs font-medium mb-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 mr-1">2</span>
                Open the portal with the current order embedded
              </p>
              <Button
                size="sm"
                onClick={openWithCodes}
                disabled={spec.length === 0}
                data-testid="autofill-open-portal-btn"
              >
                <ExternalLink size={12} className="mr-2" />
                Open RCMTM with {spec.length} fields encoded
              </Button>
              {spec.length === 0 && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  No active MTM draft. Build an order first.
                </p>
              )}
            </div>

            {/* Step 3 */}
            <div>
              <p className="text-xs font-medium mb-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 mr-1">3</span>
                On the RCMTM tab, click the bookmarklet
              </p>
              <p className="text-xs text-muted-foreground">
                A floating panel appears with every code. <strong>Click an
                input on the RCMTM form</strong>, then click <em>Fill code</em>{" "}
                or <em>Fill val</em> on the panel — the value is typed in (with
                React/Vue change events so the portal recognises it).
              </p>
            </div>

            {/* Preview */}
            {spec.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="border border-white/10 rounded-md p-3 bg-muted/20"
              >
                <p className="text-xs font-medium mb-2">Fields that will be auto-filled ({spec.length})</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {spec.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-[11px]">
                      <span className="text-muted-foreground w-32 shrink-0">{r.label}</span>
                      <span className="flex-1 truncate font-mono">{r.value}</span>
                      {r.code && <code className="text-amber-400/80 font-mono shrink-0">{r.code}</code>}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PortalAutoFill;
